import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import cn from 'classnames';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { Box, Typography } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { MetricsCard } from '../MetricsCard/MetricsCard';
import styles from './SparklineCard.module.scss';
const SPARKLINE_WIDTH = 72;
const SPARKLINE_HEIGHT = 24;
const SPARKLINE_GRADIENT_ID = 'invoice-sparkline-gradient';
export const GetSparklineCardTID = (parentTestId)=>getTestIds(parentTestId);
export const SparklineCard = (param)=>{
    let { title, tooltip, primaryValue, sparkline, trendPercent, fallbackSubtitle, isEmpty, emptyPrimary, emptySubtitle, isActive = false, isEditMode = false, isLoading = false, onRemove, dragHandle, onClick, testId } = param;
    const { SparklineCardTestIds } = useMemo(()=>GetSparklineCardTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('components.invoiceMetricsSection');
    const trendIsUp = trendPercent != null && trendPercent > 0;
    const trendIsDown = trendPercent != null && trendPercent < 0;
    const trendSubtitle = trendIsUp ? t.trend.up({
        percent: Math.round(trendPercent !== null && trendPercent !== void 0 ? trendPercent : 0)
    }) : trendIsDown ? t.trend.down({
        percent: Math.abs(Math.round(trendPercent !== null && trendPercent !== void 0 ? trendPercent : 0))
    }) : fallbackSubtitle;
    const subtitleText = isEmpty ? emptySubtitle : trendSubtitle;
    const primaryText = isEmpty ? emptyPrimary : primaryValue;
    const showSparkline = !isEmpty && sparkline.length >= 2;
    const chartData = useMemo(()=>sparkline.map((value, index)=>({
                index,
                value
            })), [
        sparkline
    ]);
    return /*#__PURE__*/ _jsx(MetricsCard, {
        title: title,
        tooltip: tooltip,
        isActive: isActive,
        isDisabled: isEmpty,
        isEditMode: isEditMode,
        isLoading: isLoading,
        onRemove: onRemove,
        dragHandle: dragHandle,
        onClick: onClick,
        testId: SparklineCardTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Box, {
            className: styles.body,
            children: [
                /*#__PURE__*/ _jsxs(Box, {
                    className: styles.valueRow,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h5",
                            weight: "normal",
                            testId: SparklineCardTestIds.PrimaryValue(),
                            className: styles.primaryValue,
                            children: primaryText
                        }),
                        showSparkline && /*#__PURE__*/ _jsx("div", {
                            "data-testid": SparklineCardTestIds.Sparkline(),
                            className: styles.sparkline,
                            style: {
                                width: SPARKLINE_WIDTH,
                                height: SPARKLINE_HEIGHT
                            },
                            children: /*#__PURE__*/ _jsx(ResponsiveContainer, {
                                width: "100%",
                                height: "100%",
                                children: /*#__PURE__*/ _jsxs(AreaChart, {
                                    data: chartData,
                                    margin: {
                                        top: 2,
                                        right: 2,
                                        bottom: 2,
                                        left: 2
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx("defs", {
                                            children: /*#__PURE__*/ _jsxs("linearGradient", {
                                                id: SPARKLINE_GRADIENT_ID,
                                                x1: "0",
                                                y1: "0",
                                                x2: "0",
                                                y2: "1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("stop", {
                                                        offset: "0%",
                                                        stopColor: colors.purple700,
                                                        stopOpacity: 0.3
                                                    }),
                                                    /*#__PURE__*/ _jsx("stop", {
                                                        offset: "100%",
                                                        stopColor: colors.purple700,
                                                        stopOpacity: 0
                                                    })
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Area, {
                                            type: "monotone",
                                            dataKey: "value",
                                            stroke: colors.purple700,
                                            strokeWidth: 1.5,
                                            fill: "url(#".concat(SPARKLINE_GRADIENT_ID, ")"),
                                            dot: false,
                                            isAnimationActive: false
                                        })
                                    ]
                                })
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph3",
                    skin: "secondary",
                    testId: SparklineCardTestIds.Subtitle(),
                    className: cn(styles.subtitle, {
                        [styles.subtitleUp]: !isEmpty && trendIsUp,
                        [styles.subtitleDown]: !isEmpty && trendIsDown
                    }),
                    children: subtitleText
                })
            ]
        })
    });
};
